// src/pages/Checkout.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { createOrder } from "../utils/firestoreService";
import COLOMBIA, { getShippingCost, getShippingLabel } from "../utils/colombiaData";
import toast from "react-hot-toast";

export default function Checkout() {
  const { items, total, dispatch } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", lastName: "", email: "", phone: "",
    department: "", city: "", address: "", postalCode: "",
    notes: "", payMethod: "transferencia"
  });
  const [ciudades, setCiudades] = useState([]);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Costo de envío dinámico según departamento
  const envio = form.department ? getShippingCost(form.department) : null;
  const envioLabel = form.department ? getShippingLabel(form.department) : null;
  const totalFinal = total + (envio || 0);

  const formatPrice = p =>
    new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(p);

  // Cargar ciudades cuando cambia departamento
  useEffect(() => {
    if (form.department) {
      const found = COLOMBIA.find(d => d.departamento === form.department);
      setCiudades(found ? found.ciudades : []);
      setForm(f => ({ ...f, city: "" }));
    } else {
      setCiudades([]);
    }
  }, [form.department]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: "" }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Requerido";
    if (!form.lastName.trim()) e.lastName = "Requerido";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email inválido";
    if (!form.phone.trim() || form.phone.trim().length < 7) e.phone = "Teléfono inválido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.department) e.department = "Selecciona un departamento";
    if (!form.city) e.city = "Selecciona una ciudad";
    if (!form.address.trim()) e.address = "Dirección requerida";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const orderDoc = await createOrder({
        customer: { name: `${form.name} ${form.lastName}`, email: form.email, phone: form.phone },
        shipping: {
          department: form.department,
          city: form.city,
          address: form.address,
          postalCode: form.postalCode,
          notes: form.notes,
          cost: envio,
          carrier: "Interrapidísimo / Coordinadora"
        },
        items: items.map(i => ({
          id: i.id, name: i.name, price: i.price,
          quantity: i.quantity, image: i.images?.[0] || "",
          variant: i.selectedVariant || null
        })),
        subtotal: total,
        shippingCost: envio,
        total: totalFinal,
        paymentMethod: form.payMethod
      });
      dispatch({ type: "CLEAR" });
      navigate(`/confirmacion/${orderDoc.id}`);
    } catch (err) {
      toast.error("Error al crear el pedido. Inténtalo de nuevo.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) { navigate("/carrito"); return null; }

  return (
    <div className="checkout-page page-pad">
      <h1>Finalizar compra</h1>

      {/* PASOS */}
      <div className="steps-indicator">
        <div className={`step-dot ${step >= 1 ? "active" : ""}`}>1<span>Datos</span></div>
        <div className="step-line" />
        <div className={`step-dot ${step >= 2 ? "active" : ""}`}>2<span>Envío</span></div>
        <div className="step-line" />
        <div className={`step-dot ${step >= 3 ? "active" : ""}`}>3<span>Pago</span></div>
      </div>

      <div className="checkout-layout">
        <div className="checkout-form">

          {/* ── PASO 1 ── */}
          {step === 1 && (
            <section className="form-section">
              <h2>Información personal</h2>
              <div className="form-row">
                <div className={`form-field ${errors.name ? "has-error" : ""}`}>
                  <label>Nombre *</label>
                  <input type="text" placeholder="Juan" value={form.name} onChange={e => set("name", e.target.value)} />
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
                <div className={`form-field ${errors.lastName ? "has-error" : ""}`}>
                  <label>Apellido *</label>
                  <input type="text" placeholder="Pérez" value={form.lastName} onChange={e => set("lastName", e.target.value)} />
                  {errors.lastName && <span className="field-error">{errors.lastName}</span>}
                </div>
              </div>
              <div className={`form-field ${errors.email ? "has-error" : ""}`}>
                <label>Correo electrónico *</label>
                <input type="email" placeholder="juan@email.com" value={form.email} onChange={e => set("email", e.target.value)} />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
              <div className={`form-field ${errors.phone ? "has-error" : ""}`}>
                <label>Teléfono / WhatsApp *</label>
                <input type="tel" placeholder="3001234567" value={form.phone} onChange={e => set("phone", e.target.value)} />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>
              <button className="btn-primary" onClick={() => { if (validateStep1()) { setErrors({}); setStep(2); } }}>
                Continuar →
              </button>
            </section>
          )}

          {/* ── PASO 2 ── */}
          {step === 2 && (
            <section className="form-section">
              <h2>Dirección de envío</h2>
              <p className="step-subtitle">Enviamos desde <strong>Armenia, Quindío</strong> con Interrapidísimo / Coordinadora.</p>

              {/* 1. Departamento */}
              <div className={`form-field ${errors.department ? "has-error" : ""}`}>
                <label>Departamento *</label>
                <select value={form.department} onChange={e => set("department", e.target.value)}>
                  <option value="">-- Selecciona un departamento --</option>
                  {COLOMBIA.map(d => (
                    <option key={d.departamento} value={d.departamento}>{d.departamento}</option>
                  ))}
                </select>
                {errors.department && <span className="field-error">{errors.department}</span>}
              </div>

              {/* Preview costo envío al elegir departamento */}
              {form.department && envio !== null && (
                <div className="shipping-preview">
                  <div className="shipping-preview-info">
                    <span>🚚 {envioLabel.label}</span>
                    <span className="shipping-preview-days">⏱ {envioLabel.days}</span>
                  </div>
                  <span className="shipping-preview-price">{formatPrice(envio)}</span>
                </div>
              )}

              {/* 2. Ciudad */}
              <div className={`form-field ${errors.city ? "has-error" : ""}`}>
                <label>Ciudad / Municipio *</label>
                <select value={form.city} onChange={e => set("city", e.target.value)} disabled={!form.department}>
                  <option value="">
                    {form.department ? "-- Selecciona una ciudad --" : "-- Primero selecciona un departamento --"}
                  </option>
                  {ciudades.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.city && <span className="field-error">{errors.city}</span>}
              </div>

              {/* 3. Dirección */}
              <div className={`form-field ${errors.address ? "has-error" : ""}`}>
                <label>Dirección completa *</label>
                <input
                  type="text"
                  placeholder="Calle 123 # 45-67, Apto 8"
                  value={form.address}
                  onChange={e => set("address", e.target.value)}
                />
                {errors.address && <span className="field-error">{errors.address}</span>}
              </div>

              <div className="form-field">
                <label>Código postal (opcional)</label>
                <input type="text" placeholder="630001" value={form.postalCode} onChange={e => set("postalCode", e.target.value)} />
              </div>

              <div className="form-field">
                <label>Notas para el envío (opcional)</label>
                <textarea
                  placeholder="Instrucciones especiales, referencias del lugar..."
                  value={form.notes}
                  onChange={e => set("notes", e.target.value)}
                  rows={3}
                />
              </div>

              <div className="step-buttons">
                <button className="btn-ghost" onClick={() => { setErrors({}); setStep(1); }}>← Atrás</button>
                <button className="btn-primary" onClick={() => { if (validateStep2()) { setErrors({}); setStep(3); } }}>
                  Continuar →
                </button>
              </div>
            </section>
          )}

          {/* ── PASO 3 ── */}
          {step === 3 && (
            <section className="form-section">
              <h2>Método de pago</h2>
              <div className="pay-methods">
                {[
                  { id: "transferencia", label: "Transferencia / Nequi / Daviplata", desc: "Transfiere y envía tu comprobante de pago", icon: "🏦", active: true },
                  { id: "contraentrega", label: "Pago contra entrega", desc: "Pagas en efectivo al recibir tu pedido", icon: "🚚", active: true },
                  { id: "payu", label: "PayU — Tarjeta / PSE", desc: "Próximamente disponible", icon: "💳", active: false },
                  { id: "mercadopago", label: "Mercado Pago", desc: "Próximamente disponible", icon: "💰", active: false }
                ].map(m => (
                  <label key={m.id} className={`pay-option ${form.payMethod === m.id ? "selected" : ""} ${!m.active ? "pay-disabled" : ""}`}>
                    <input
                      type="radio" name="payMethod" value={m.id}
                      checked={form.payMethod === m.id}
                      disabled={!m.active}
                      onChange={() => m.active && set("payMethod", m.id)}
                    />
                    <span className="pay-icon">{m.icon}</span>
                    <div>
                      <strong>{m.label}</strong>
                      <span>{m.desc}</span>
                    </div>
                    {!m.active && <span className="coming-soon-badge">Próximo</span>}
                  </label>
                ))}
              </div>

              {form.payMethod === "transferencia" && (
                <div className="transfer-info">
                  <h4>📲 Datos para transferencia</h4>
                  <p><strong>Nequi / Daviplata:</strong> 300 000 0000</p>
                  <p><strong>Bancolombia:</strong> Cta. Ahorros 123-456789-00</p>
                  <p><strong>A nombre de:</strong> Con el Corazón</p>
                  <p className="transfer-note">
                    Envía el comprobante a <a href="mailto:pagos@conelcorazon.co">pagos@conelcorazon.co</a> con tu número de pedido para procesar el envío.
                  </p>
                </div>
              )}
              {form.payMethod === "contraentrega" && (
                <div className="transfer-info">
                  <h4>🚚 Pago contra entrega</h4>
                  <p>Pagarás en efectivo cuando recibas tu pedido.</p>
                  <p className="transfer-note">Para zonas rurales o municipios pequeños consulta disponibilidad por WhatsApp antes de hacer el pedido.</p>
                </div>
              )}

              <div className="step-buttons">
                <button className="btn-ghost" onClick={() => { setErrors({}); setStep(2); }}>← Atrás</button>
                <button className="btn-primary btn-big" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Procesando..." : `Confirmar pedido · ${formatPrice(totalFinal)}`}
                </button>
              </div>
            </section>
          )}
        </div>

        {/* RESUMEN */}
        <aside className="checkout-summary">
          <h3>Tu pedido</h3>
          <div className="summary-items">
            {items.map(i => (
              <div key={i.id} className="summary-item">
                <img src={i.images?.[0] || "/placeholder.jpg"} alt={i.name} onError={e => { e.target.src = "/placeholder.jpg"; }} />
                <div>
                  <span>{i.name}</span>
                  {i.selectedVariant && <span className="summary-variant">· {i.selectedVariant}</span>}
                  <span>x{i.quantity}</span>
                </div>
                <span>{formatPrice(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="summary-row"><span>Subtotal</span><span>{formatPrice(total)}</span></div>
          <div className="summary-row">
            <span>Envío {form.department ? `(${form.department})` : ""}</span>
            <span>{envio !== null ? formatPrice(envio) : "—"}</span>
          </div>
          {envioLabel && (
            <p className="shipping-hint">⏱ {envioLabel.days}</p>
          )}
          {!form.department && (
            <p className="shipping-hint">Selecciona tu departamento para ver el costo de envío</p>
          )}
          <div className="summary-total">
            <span>Total</span>
            <span>{envio !== null ? formatPrice(totalFinal) : "—"}</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
